
import {Component, Provide, Vue, Watch} from "vue-property-decorator";
import {MeDto, logout as logoutRequest} from "@/api";
import AccountNotSetUp from "@/components/AccountNotSetUp.vue";
import i18n from "@/plugins/translations/t";
import FeedbackButton from "@/components/FeedbackButton.vue";

@Component({
    components: {AccountNotSetUp, FeedbackButton},
})
export default class App extends Vue {

    private currentView: string = this.$route.name ?? "home";

    drawer = false;

    /** The logged-in user, loaded into the store by `loadUser` (`GET /api/me`). `null` when nobody is logged in. */
    get user(): MeDto | null {
        return this.$store.state.user;
    }

    get authenticated() {
        return this.user !== null;
    }

    /**
     * True until the first `GET /api/me` has settled, and while logging in, so that no page mounts with `user === null`.
     * A later `reloadUser` refreshes the store silently and leaves the mounted page alone.
     */
    get loading() {
        return this.$store.state.userLoading && this.user === null;
    }

    get isMobile() {
        return this.$vuetify.breakpoint.name === "xs";
    }

    get isDrawer() {
        return !this.isMobile || this.drawer;
    }

    set isDrawer(val: boolean) {
        this.drawer = !this.isMobile || val;
    }

    get pictureUrl(): string {
        return this.user?.profilePictureUrl ?? "";
    }

    get currentLang() {
        return i18n.locale;
    }

    set currentLang(val: string) {
        i18n.locale = val
        this.$router.push({name: this.$route.name ?? "", params: {...this.$route.params, lang: val}})
    }

    created() {
        if (window.location.pathname.startsWith("/en")) {
            i18n.locale = "en";
        }

        if (window.location.pathname.startsWith("/et")) {
            i18n.locale = "et";
        }

        this.$store.dispatch("loadUser");
    }

    private languages = [
        {
            text: "Eesti",
            value: "et"
        },
        {
            text: "English",
            value: "en"
        }
    ]



    /** Reloads the current user (`GET /api/me`). Resolves with the user, or `null` when the call failed. */
    @Provide("reloadUser")
    reloadUser(): Promise<MeDto | null> {
        return this.$store.dispatch("loadUser");
    }

    @Watch("user", {deep: true})
    userChanged() {
        if (!this.user) {
            this.goToLogin();
        }

        this.leaveLoginWhenLoggedIn();
    }

    /** A logged-in user has nothing to do on the login page. Does nothing without a user (a 401 clears it first). */
    private leaveLoginWhenLoggedIn() {
        if (this.user && this.$route.name === "login") {
            this.$router.push({name: "home", params: {lang: this.$route.params.lang}}).catch(() => undefined);
        }
    }

    /** Logged in, but neither an admin nor assigned to a partner. */
    get showOverlay(): boolean {
        return this.user !== null && !this.$store.getters.hasAccess;
    }

    private goToLogin() {
        if (this.$route.name !== "login") {
            this.$router.push({name: "login", params: {lang: this.$route.params.lang ?? i18n.locale}}).catch(() => undefined);
        }
    }

    @Watch("$route")
    routeChanged() {
        this.currentView = this.$route.name ?? "home"

        i18n.locale = this.$route.params.lang ?? "et";

        // A page that is opened directly (a new tab on /login) resolves its route after the user has been loaded, because
        // its component is a lazy chunk: `userChanged` saw no login route yet, so this is where the user leaves it.
        this.leaveLoginWhenLoggedIn();
    }

    @Watch("currentView")
    currentViewChanged(curr: string | undefined, prev: string | undefined) {
        this.isDrawer = false;

        if (curr === undefined || prev === undefined) {

            if (prev !== undefined) {
                this.currentView = prev;
            }

            return;
        }

        if (curr !== prev && this.$route.name !== curr) {
            this.$router.push({name: curr, params: {lang: this.$route.params.lang}})
        }
    }


    async logout() {
        try {
            await logoutRequest();
        } catch (e) {
            // The cookie was not cleared, so the user is still logged in: keep the session and the page as they are.
            // The http interceptor has already shown the failure in the snackbar.
            return;
        }

        window.localStorage.setItem("hasLoggedIn", "");
        this.$store.commit("resetUser");
        this.goToLogin();
    }

    set snackbar(val: boolean) {
        if (!val) {
            this.$store.commit("hideSnackbar");
        }
    }

    get snackbar() {
        return this.$store.state.snackbar
    }

    get snackbarText() {
        return this.$store.state.snackbarText;
    }

    get snackbarColor() {
        return this.$store.state.isError ? "error" : "";
    }

    get timeout() {
        return this.$store.state.timeout ?? 2000;
    }

};
